import Home from './pages/Home/Home';
import Analyses from './pages/Analyses/Analyses';
import Votings from './pages/Votings/Votings';
import Meetings from './pages/Meetings/Meetings';
import App, { AppLoader } from './App';
import MeetingDetail from './pages/Meetings/MeetingDetail';
import PartyDetail from './pages/Parties/PartyDetail';
import MunicipalityHelmet from './pages/MunicipalityHelmet';
import NotFound from './pages/NotFound/NotFound';
import VotingDetail from './pages/Votings/VotingDetail';
import MemberDetail from './pages/Members/MemberDetail';
import Organy from './pages/Bodies/Bodies';
import Parties from './pages/Parties/Parties';
import Members from './pages/Members/Members';
import { AllMunicipalitiesHome } from './pages/AllMunicipalitiesHome/AllMunicipalitiesHome';
import MembersComparison from './pages/Analyses/MembersComparison';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet } from 'react-router-dom';
import {
    RootLoader,
    MunicipalityHelmetLoader,
    MemberLoader,
    HomeLoader,
    VotingsLoader,
    VotingDetailLoader,
    CouncilCompositionLoader,
    MeetingsLoader,
    MeetingDetailLoader,
    PartyLoader,
    Home2Loader,
    LoadRegions
} from './Loaders';
import { DatasetsLoader } from './loaders/datasetsLoader';
import AttendanceAnalysis from './pages/Analyses/AttendanceAnalysis';
import PartiesUnity from './pages/Analyses/PartiesUnity';
import VotingUnity from './pages/Analyses/VotingUnity';
import AppLayout from './layouts/AppLayout';
import { About } from './pages/About/About';
import { Datasets } from './pages/Datasets/Datasets';
import { Partners } from './pages/Partners/Partners';
import { Stats } from './pages/Stats/Stats';
import { SINGLE_MUNICIPALITY_ID } from './Constants';

// When SINGLE_MUNICIPALITY_ID is set, this subtree is mounted directly at '/' instead
// of under '/:municipalitaId' - see src/utils/councilParams.ts. Body type and council
// term keep working exactly as they do today (Organy's body picker / auto-redirect to
// the latest term, the ':bodyType'/':zastupitelstvoId' segments).
const municipalityRoute: RouteObject = {
    id: 'municipality',
    element: <MunicipalityHelmet />,
    loader: MunicipalityHelmetLoader,
    errorElement: <NotFound />,
    children: [
        {
            index: true,
            element: <Organy />,
            //loader: async ({params}) => {return},
            errorElement: <NotFound />
        },
        {
            path: ':bodyType',
            element: <Outlet />,
            //loader: async ({params}) => {return},
            errorElement: <NotFound />,
            children: [
                {
                    path: ':zastupitelstvoId',
                    element: <App />,
                    loader: AppLoader,
                    errorElement: <NotFound />,
                    children: [
                        {
                            index: true,
                            element: <Home />,
                            loader: Home2Loader,
                            errorElement: <NotFound />
                        },
                        {
                            path: 'hlasovani',
                            element: <Outlet />,
                            errorElement: <NotFound />,
                            children: [
                                {
                                    index: true,
                                    element: <Votings />,
                                    loader: VotingsLoader,
                                    errorElement: <NotFound />
                                },
                                {
                                    path: ':hlasovaniId',
                                    element: <VotingDetail />,
                                    loader: VotingDetailLoader,
                                    errorElement: <NotFound />
                                }
                            ]
                        },
                        {
                            path: 'subjekty',
                            element: <Outlet />,
                            errorElement: <NotFound />,
                            children: [
                                {
                                    index: true,
                                    element: <Parties />,
                                    loader: CouncilCompositionLoader,
                                    errorElement: <NotFound />
                                },
                                {
                                    path: ':subjektId',
                                    element: <PartyDetail />,
                                    loader: PartyLoader,
                                    errorElement: <NotFound />
                                }
                            ]
                        },
                        {
                            path: 'zasedani',
                            element: <Outlet />,
                            errorElement: <NotFound />,
                            children: [
                                {
                                    index: true,
                                    element: <Meetings />,
                                    loader: MeetingsLoader,
                                    errorElement: <NotFound />
                                },
                                {
                                    path: ':zasedaniId',
                                    element: <MeetingDetail />,
                                    loader: MeetingDetailLoader,
                                    errorElement: <NotFound />
                                }
                            ]
                        },
                        {
                            path: 'zastupitele',
                            element: <Outlet />,
                            errorElement: <NotFound />,
                            children: [
                                {
                                    index: true,
                                    element: <Members />,
                                    loader: CouncilCompositionLoader,
                                    errorElement: <NotFound />
                                },
                                {
                                    path: ':zastupitelId',
                                    element: <MemberDetail />,
                                    loader: MemberLoader,
                                    errorElement: <NotFound />
                                }
                            ]
                        },
                        {
                            path: 'analyzy',
                            element: <Analyses />,
                            errorElement: <NotFound />,
                            children: [
                                {
                                    index: true,
                                    element: <Navigate to='dochazka' replace />
                                },
                                {
                                    path: 'dochazka',
                                    element: <AttendanceAnalysis />,
                                    errorElement: <NotFound />
                                },
                                {
                                    path: 'porovnani_zastupitelu',
                                    element: <MembersComparison />,
                                    errorElement: <NotFound />
                                },
                                {
                                    path: 'nejednotnost',
                                    element: <PartiesUnity />,
                                    errorElement: <NotFound />
                                },
                                {
                                    path: 'koheze',
                                    element: <VotingUnity />,
                                    errorElement: <NotFound />
                                }
                            ]
                        },
                        {
                            path: '*',
                            element: <NotFound customError={404} />
                        }
                    ]
                }
            ]
        }
    ]
};

if (!SINGLE_MUNICIPALITY_ID) {
    municipalityRoute.path = ':municipalitaId';
}

const rootChildren: RouteObject[] = [];

if (!SINGLE_MUNICIPALITY_ID) {
    rootChildren.push({
        index: true,
        element: <AllMunicipalitiesHome />,
        loader: HomeLoader,
        errorElement: <NotFound />
    });
}

rootChildren.push(
    municipalityRoute,
    {
        path: 'datasety',
        element: <Datasets />,
        loader: DatasetsLoader,
        errorElement: <NotFound />
    },
    {
        path: 'spoluprace',
        element: <Partners />,
        errorElement: <NotFound />
    },
    {
        path: 'o-projektu',
        element: <About />,
        errorElement: <NotFound />
    },
    {
        path: 'stats',
        element: <Stats />,
        loader: LoadRegions,
        shouldRevalidate: () => false,
        errorElement: <NotFound />
    },
    {
        path: '*',
        element: <NotFound customError={404} />
    }
);

const routes: RouteObject[] = [
    {
        path: '/',
        id: 'root',
        loader: RootLoader,
        element: <AppLayout />,
        errorElement: <NotFound />,
        children: rootChildren
    }
];

export default routes;
